// src/pages/AccountPage.jsx (or Dashboard.js)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../supabaseClient";

/* ─────────────────────────────────────────────────────────────
   Optional: friendly labels shown in the Change Plan dropdown
────────────────────────────────────────────────────────────── */
const PLAN_LABELS = {
  nascar_lite: "NASCAR Lite ($30/mo)",
  nfl_lite:    "NFL Lite ($30/mo)",
  mlb_lite:    "MLB Lite ($30/mo)",
  nba_lite:    "NBA Lite ($30/mo)",
  nascar_pro:  "NASCAR Pro ($40/mo)",
  nfl_pro:     "NFL Pro ($40/mo)",
  mlb_pro:     "MLB Pro ($40/mo)",
  nba_pro:     "NBA Pro ($40/mo)",
  all_access_lite: "All-Access Lite",
  all_access_pro:  "All-Access Pro",
  discord_only: "Discord Only",
};

/* ─────────────────────────────────────────────────────────────
   Minimal Toast System (no libraries)
────────────────────────────────────────────────────────────── */
function useToasts() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const showToast = (message, variant = "info", durationMs = 3000) => {
    const id = ++idRef.current;
    const toast = { id, message, variant, leaving: false };
    setToasts((t) => [...t, toast]);
    setTimeout(() => {
      setToasts((t) =>
        t.map((it) => (it.id === id ? { ...it, leaving: true } : it))
      );
      setTimeout(
        () => setToasts((t) => t.filter((it) => it.id !== id)),
        300
      );
    }, durationMs);
  };

  const removeNow = (id) => {
    setToasts((t) =>
      t.map((it) => (it.id === id ? { ...it, leaving: true } : it))
    );
    setTimeout(
      () => setToasts((t) => t.filter((it) => it.id !== id)),
      250
    );
  };

  return { toasts, showToast, removeNow };
}

function ToastViewport({ toasts, removeNow }) {
  const stylesByVariant = useMemo(
    () => ({
      info: "bg-slate-900 text-white",
      success: "bg-blue-600 text-white",
      error: "bg-red-600 text-white",
      warn: "bg-amber-600 text-white",
    }),
    []
  );

  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[1000] flex w-full max-w-sm flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={[
            "pointer-events-auto rounded-xl px-4 py-3 shadow-lg ring-1 ring-black/10 transition-all",
            "flex items-center justify-between gap-3",
            stylesByVariant[t.variant] || stylesByVariant.info,
            t.leaving ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100",
          ].join(" ")}
          role="status"
        >
          <div className="text-sm">{t.message}</div>
          <button
            onClick={() => removeNow(t.id)}
            className="rounded-md bg-white/10 px-2 py-1 text-xs hover:bg-white/20"
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [resuming, setResuming] = useState(false);
  const [restartPaymentPending, setRestartPaymentPending] = useState(
    () => new URLSearchParams(window.location.search).get("restart") === "returned"
  );
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");

  // ensure-role UI
  const [ensuring, setEnsuring] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugJson, setDebugJson] = useState(null);

  const { toasts, showToast, removeNow } = useToasts();

  // ─────────────────────────────────────────────────────────────
  // Load user + profile
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user || null);

      if (user) {
        const { data, error } = await supabase
          .from("profiles")
          .select(
            [
              "username",
              "plan",
              "status",
              "created_at",
              "stripe_customer_id",
              "discord_id",
              "discord_username",
              "discord_connected_at",
              "paypal_subscription_id",
              "paypal_status",
              "paypal_next_bill",
              "paypal_plan_id",
              "access_until",
            ].join(", ")
          )
          .eq("id", user.id)
          .single();

        if (!error) {
          setProfile(data);
          setUsernameInput(data?.username || "");
        }
      }
      setLoading(false);
    })();
  }, []);

  // On the PayPal return, wait for the verified payment webhook to grant access.
  // The browser return URL and subscription approval are not payment proof.
  useEffect(() => {
    if (!user?.id || !restartPaymentPending) return;
    let cancelled = false;
    let timer;
    let attempts = 0;
    async function refreshRestart() {
      try {
        const res = await callManage("restart-status");
        if (cancelled) return;
        if (res.ok && res.profile) {
          setProfile((p) => ({ ...p, ...res.profile }));
          if (!res.paymentPending && res.profile.paypal_status === "ACTIVE" && res.profile.plan !== "free") {
            setRestartPaymentPending(false);
            showToast("Payment confirmed. Your membership is active.", "success", 5000);
            return;
          }
          if (!res.paymentPending) {
            setRestartPaymentPending(false);
            showToast("Checkout ended without a confirmed payment. You can try Resume billing again.", "info", 6000);
            return;
          }
        }
      } catch (_) {
        // A temporary connection failure must not grant access or create another charge.
      }
      attempts += 1;
      if (!cancelled && attempts < 20) timer = setTimeout(refreshRestart, 3000);
    }
    refreshRestart();
    return () => { cancelled = true; clearTimeout(timer); };
    // callManage/showToast are local helpers; polling is scoped to this user and flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, restartPaymentPending]);

  // ─────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────
  function fmt(d) {
    try {
      return new Date(d).toLocaleString();
    } catch {
      return "—";
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Auth
  // ─────────────────────────────────────────────────────────────
  async function signOut() {
    await supabase.auth.signOut();
    window.location.href = "/login";
  }

  // ─────────────────────────────────────────────────────────────
  // Discord connect/disconnect
  // ─────────────────────────────────────────────────────────────
  async function connectDiscord() {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      showToast("Please log in first.", "warn");
      return;
    }
    const sb = encodeURIComponent(session.access_token);
    window.location.href = `/.netlify/functions/auth-discord?sb=${sb}`;
  }

  async function disconnectDiscord() {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        discord_id: null,
        discord_username: null,
        discord_connected_at: null,
      })
      .eq("id", user.id);

    if (!error) {
      setProfile((p) =>
        p
          ? {
              ...p,
              discord_id: null,
              discord_username: null,
              discord_connected_at: null,
            }
          : p
      );
      showToast("Discord disconnected.", "success");
    } else {
      showToast("Could not disconnect Discord.", "error");
    }
    setSaving(false);
  }

  // ─────────────────────────────────────────────────────────────
  // Username
  // ─────────────────────────────────────────────────────────────
  async function saveUsername() {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ username: usernameInput || null })
      .eq("id", user.id);
    setSaving(false);
    if (error) {
      showToast(`Could not save username: ${error.message}`, "error", 5000);
    } else {
      setProfile((p) => (p ? { ...p, username: usernameInput || null } : p));
      showToast("Username saved.", "success");
    }
  }

  // ─────────────────────────────────────────────────────────────
  // PayPal manage API (pause/resume/cancel/details)
  // ─────────────────────────────────────────────────────────────
  async function callManage(action, reason) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      showToast("Please log in first.", "warn");
      return { ok: false, error: "No session" };
    }

    const res = await fetch("/.netlify/functions/paypal-manage", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action, reason }),
    });

    let json = {};
    try {
      json = await res.json();
    } catch {}
    if (!res.ok) return { ok: false, error: json?.error || "Request failed" };
    return { ok: true, ...json };
  }

  // ─────────────────────────────────────────────────────────────
  // Change plan (revise)
  // ─────────────────────────────────────────────────────────────
  async function changePlan(targetAppPlan) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return showToast("Please log in first.", "warn");

    const res = await fetch("/.netlify/functions/paypal-change-plan", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        targetAppPlan,
        returnUrl: window.location.origin + "/account",
        cancelUrl: window.location.origin + "/account",
      }),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      showToast(json?.error || "Plan change failed", "error", 6000);
      return;
    }

    if (json.url) {
      window.location.href = json.url; // PayPal approval
    } else {
      showToast("Plan change submitted. Access updates when PayPal confirms.", "info", 5000);
      window.location.reload();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Ensure Discord role
  // ─────────────────────────────────────────────────────────────
  async function ensureDiscordRole({ showMessages = false, debug = false } = {}) {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        if (showMessages) showToast("Please log in first.", "warn");
        return { ok: false, message: "No active Supabase session" };
      }

      setEnsuring(true);
      setDebugJson(null);

      const url = debug
        ? "/.netlify/functions/discord-ensure-role?debug=1"
        : "/.netlify/functions/discord-ensure-role";

      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      const json = await res.json().catch(() => ({}));

      if (debug) {
        setDebugJson({ httpStatus: res.status, ...json });
      }

      if (!res.ok || json?.ok === false) {
        const message =
          json?.message ||
          json?.error ||
          `Discord role sync failed (${res.status})`;

        console.error("[Discord role sync failed]", {
          status: res.status,
          response: json,
        });

        if (showMessages) showToast(message, "error", 7000);

        return {
          ok: false,
          status: res.status,
          ...json,
          message,
        };
      }

      if (showMessages) showToast("Discord role synced!", "success");

      return {
        ok: true,
        status: res.status,
        ...json,
      };
    } catch (e) {
      const message = e?.message || "Discord role request failed";
      if (showMessages) showToast(message, "error", 7000);
      return { ok: false, message };
    } finally {
      setEnsuring(false);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Auto-ensure after connect (?discord=linked)
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const url = new URL(window.location.href);

      if (url.searchParams.get("discord") !== "linked") {
        return;
      }

      const result = await ensureDiscordRole({
        showMessages: false,
        debug: false,
      });

      url.searchParams.delete("discord");
      window.history.replaceState({}, "", url.toString());

      if (result?.ok) {
        showToast(
          "Discord connected and your role was updated.",
          "success",
          4500
        );
      } else {
        showToast(
          result?.message ||
            "Discord connected, but we couldn't assign your server role. Use ‘Fix my Discord role’ below.",
          "error",
          8000
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return null;

  // Derived values
  const plan = profile?.plan ?? "free";
  const status = profile?.status ?? "inactive";
  const paypalStatus = profile?.paypal_status || "—";
  const nextBill = profile?.paypal_next_bill ? fmt(profile.paypal_next_bill) : "—";
  const accessUntilText = profile?.access_until ? fmt(profile.access_until) : null;
  const discordConnected = Boolean(profile?.discord_id);
  const hasFutureAccess =
    profile?.access_until && new Date(profile.access_until) > new Date();

  const planLabel =
    plan === "free" ? "Free" : PLAN_LABELS[plan] || plan;

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-[#050816] via-[#050F26] to-[#020617] text-slate-100">
        {/* Hero / Header */}
        <section className="relative border-b border-slate-800/80 bg-gradient-to-br from-[#050816] via-[#050F26] to-[#020617]">
          <div className="pointer-events-none absolute inset-0 opacity-50">
            <div className="absolute -top-28 left-6 h-60 w-60 rounded-full bg-blue-500/25 blur-3xl" />
            <div className="absolute bottom-[-120px] right-[-40px] h-72 w-72 rounded-full bg-blue-700/25 blur-3xl" />
          </div>

          <div className="relative mx-auto flex max-w-5xl items-center justify-between px-4 py-8 sm:px-6 lg:px-8">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/40 bg-slate-950/80 px-3 py-1 text-[11px] font-medium text-blue-100 shadow-[0_0_25px_rgba(37,99,235,0.4)]">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
                Cpenn DFS Member
              </div>

              <h1 className="mt-4 text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Account & Membership
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                Manage your plan, billing, and Discord access all in one place.
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs sm:text-sm">
                {/* Plan badge */}
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-900/80 px-3 py-1 ring-1 ring-slate-700/80">
                  <span className="h-2 w-2 rounded-full bg-blue-400" />
                  <span className="font-medium text-slate-50">{planLabel}</span>
                </div>

                {/* Status badge */}
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-900/80 px-3 py-1 ring-1 ring-slate-700/80">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      status === "active" ? "bg-blue-400" : "bg-amber-400"
                    }`}
                  />
                  <span className="font-medium text-slate-50">
                    {status === "active" ? "Status: Active" : `Status: ${status}`}
                  </span>
                </div>

                {/* Existing cancel badge */}
                {paypalStatus === "CANCELLED" && hasFutureAccess && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1 text-amber-100 ring-1 ring-amber-400/70">
                    <span className="h-2 w-2 rounded-full bg-amber-300" />
                    <span className="font-medium text-xs sm:text-[11px]">
                      Cancels on {accessUntilText}
                    </span>
                  </div>
                )}

                {/* NEW paused badge */}
                {paypalStatus === "SUSPENDED" && (
                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1 text-amber-100 ring-1 ring-amber-400/70">
                    <span className="h-2 w-2 rounded-full bg-amber-300" />
                    <span className="font-medium text-xs sm:text-[11px]">
                      Paused{accessUntilText ? ` — access until ${accessUntilText}` : ""}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={signOut}
              className="hidden sm:inline-flex items-center rounded-full border border-slate-600/70 bg-slate-950/70 px-3 py-1.5 text-xs font-medium text-slate-100 shadow-sm hover:border-red-400/70 hover:text-red-100 hover:shadow-[0_0_25px_rgba(248,113,113,0.4)]"
            >
              Logout
            </button>
          </div>
        </section>

        {/* Main content */}
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-800/80 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.9)]">
            {/* Mobile logout */}
            <div className="mb-4 flex items-center justify-between sm:hidden">
              <div className="text-xs text-slate-400">
                Logged in as{" "}
                <span className="font-semibold text-slate-100">
                  {profile?.username || user?.email}
                </span>
              </div>
              <button
                onClick={signOut}
                className="inline-flex items-center rounded-full border border-slate-600/70 bg-slate-950/70 px-3 py-1 text-[11px] font-medium text-slate-100 hover:border-red-400/70 hover:text-red-100"
              >
                Logout
              </button>
            </div>

            {/* Info grid */}
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Account card */}
              <div className="rounded-xl border border-slate-800/80 bg-slate-900/80 p-4 shadow-inner">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  Account
                </div>
                <div className="mt-2 space-y-3 text-sm text-slate-100">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-slate-400">Email</span>
                    <span className="font-medium">{user?.email}</span>
                  </div>

                  <div className="flex flex-col gap-2">
                    <span className="text-xs text-slate-400">Username</span>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        value={usernameInput}
                        onChange={(e) => setUsernameInput(e.target.value)}
                        className="w-full max-w-[180px] rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Add a username"
                      />
                      <button
                        onClick={saveUsername}
                        disabled={saving}
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white shadow hover:bg-blue-500 disabled:opacity-60"
                      >
                        Save
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-slate-400">Member since</span>
                    <span className="font-medium">
                      {profile?.created_at ? fmt(profile.created_at) : "—"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Billing / Subscription card */}
              <div className="rounded-xl border border-slate-800/80 bg-slate-900/80 p-4 shadow-inner">
                <div className="text-[11px] uppercase tracking-wide text-slate-400">
                  Billing
                </div>
                <div className="mt-2 space-y-3 text-sm text-slate-100">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-slate-400">PayPal status</span>
                    <span className="font-medium">{paypalStatus}</span>
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-slate-400">Next bill date</span>
                    <span className="font-medium">{nextBill}</span>
                  </div>

                  {/* UPDATED: show access-until for CANCELLED OR SUSPENDED */}
                  {(paypalStatus === "CANCELLED" || paypalStatus === "SUSPENDED") && accessUntilText && (
                    <div className="flex flex-col gap-0.5 text-amber-100">
                      <span className="text-xs text-amber-300">Access until</span>
                      <span className="font-medium">{accessUntilText}</span>
                    </div>
                  )}

                  {restartPaymentPending && (
                    <div className="rounded-lg border border-blue-400/40 bg-blue-500/10 p-3 text-sm text-blue-100" role="status">
                      Waiting for PayPal payment confirmation. Access returns after payment succeeds.
                      If this takes more than a minute, refresh this page or contact support before starting another checkout.
                    </div>
                  )}
                  {paypalStatus === "SUSPENDED" && !hasFutureAccess && (
                    <p className="text-xs text-slate-300">
                      Resume opens PayPal checkout for a new monthly subscription without a free trial.
                      Your membership starts after payment succeeds, with a new monthly billing cycle.
                    </p>
                  )}

                  {/* Actions */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a
                      href="/pricing"
                      className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-blue-500"
                    >
                      Upgrade
                    </a>

                    <button
                      onClick={() =>
                        window.open(
                          "https://www.paypal.com/myaccount/autopay/",
                          "_blank"
                        )
                      }
                      className="inline-flex items-center rounded-md border border-slate-600/80 bg-slate-950/60 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-900"
                    >
                      Manage in PayPal
                    </button>

                    {/* NEW: Pause button (only when ACTIVE) */}
                    {plan !== "free" && paypalStatus === "ACTIVE" && (
                      <button
                        onClick={async () => {
                          const res = await callManage("suspend", "User requested pause");
                          if (res.ok) {
                            setProfile((p) =>
                              p
                                ? {
                                    ...p,
                                    paypal_status: "SUSPENDED",
                                    access_until: res.access_until || p.access_until || null,
                                    paypal_next_bill: res.access_until || p.paypal_next_bill || null,
                                  }
                                : p
                            );
                            showToast(
                              "Paused. Billing is stopped; you’ll keep access until the end of the current period.",
                              "info",
                              6000
                            );
                          } else {
                            showToast(res.error || "Couldn’t pause right now.", "error", 6000);
                          }
                        }}
                        className="inline-flex items-center rounded-md border border-amber-400/70 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-amber-100 hover:bg-amber-500/20"
                      >
                        Pause billing
                      </button>
                    )}

                    {/* NEW: Resume button (only when SUSPENDED) */}
                    {profile?.paypal_subscription_id && paypalStatus === "SUSPENDED" && (
                      <button
                        onClick={async () => {
                          if (resuming) return;
                          setResuming(true);
                          try {
                            const res = await callManage("activate", "User reactivated");
                            if (res.ok && res.needsCheckout && res.url) {
                              window.location.assign(res.url);
                              return;
                            }
                            if (res.ok && res.paymentPending) {
                              setRestartPaymentPending(true);
                              showToast("Waiting for payment confirmation. Your membership will restart after payment succeeds.", "info", 6000);
                              return;
                            }
                            if (res.ok) {
                              setProfile((p) =>
                                p
                                  ? {
                                      ...p,
                                      plan: res.plan,
                                      status: res.status,
                                      paypal_plan_id: res.paypal_plan_id,
                                      paypal_status: res.paypal_status,
                                      access_until: null,
                                      paypal_next_bill: res.paypal_next_bill || null,
                                    }
                                  : p
                              );
                              showToast("Resumed. Your plan access is restored and billing is active.", "success", 4500);
                            } else {
                              showToast(res.error || "Couldn’t resume right now.", "error", 6000);
                            }
                          } catch (_) {
                            showToast("Couldn’t confirm the resume. Please refresh and check your subscription status before trying again.", "error", 6000);
                          } finally {
                            setResuming(false);
                          }
                        }}
                        disabled={resuming}
                        className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-emerald-500"
                      >
                        {resuming ? "Resuming…" : "Resume billing"}
                      </button>
                    )}

                    {/* Existing cancel behavior unchanged */}
                    {plan !== "free" && (
                      <button
                        onClick={async () => {
                          if (!profile?.paypal_subscription_id) {
                            window.open("https://www.paypal.com/myaccount/autopay/", "_blank");
                            return;
                          }
                          const res = await callManage("cancel", "User requested cancel");
                          if (res.ok) {
                            setProfile((p) =>
                              p
                                ? {
                                    ...p,
                                    paypal_status: "CANCELLED",
                                    access_until: res.access_until || p.access_until || null,
                                  }
                                : p
                            );
                            showToast(
                              "Cancelled. You’ll keep access until the end of the current period.",
                              "info",
                              6000
                            );
                          } else {
                            showToast(
                              res.error ||
                                "Couldn’t cancel here. You can cancel in PayPal instead.",
                              "error",
                              6000
                            );
                          }
                        }}
                        className="inline-flex items-center rounded-md border border-red-400/70 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-100 hover:bg-red-500/20"
                      >
                        Cancel subscription
                      </button>
                    )}
                  </div>

                  {/* Change Plan notice */}
                  {profile?.paypal_subscription_id && (
                    <div className="mt-4 rounded-lg bg-slate-950/70 p-3 ring-1 ring-slate-800/90">
                      <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">
                        Change plan
                      </div>

                      <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-3 text-sm text-amber-100">
                        To change plans, please cancel before your next billing date. Your current access will remain active until the date shown above. After it expires, sign up for the new tier.
                      </div>

                      <p className="mt-2 text-[11px] text-slate-400">
                        Need help switching sooner? Please use the Contact page.
                      </p>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <a
                          href="/contact"
                          className="inline-flex items-center rounded-md border border-slate-600 bg-slate-950 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-900"
                        >
                          Contact us
                        </a>

                        <a
                          href="/pricing"
                          className="inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-blue-500"
                        >
                          View plans
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Discord card */}
              <div className="sm:col-span-2 rounded-xl border border-slate-800/80 bg-slate-900/80 p-4 shadow-inner">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">
                      Discord
                    </div>
                    <div className="mt-2 text-sm text-slate-100">
                      <div className="flex flex-col gap-1">
                        <div>
                          <span className="text-xs text-slate-400">Status</span>
                          <div className="mt-0.5">
                            {discordConnected ? (
                              <span className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-100 ring-1 ring-blue-400/70">
                                <span className="h-2 w-2 rounded-full bg-blue-300" />
                                Connected
                                {profile?.discord_username ? ` as ${profile.discord_username}` : ""}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-2 rounded-full bg-slate-800/80 px-3 py-1 text-xs font-medium text-slate-200 ring-1 ring-slate-700">
                                <span className="h-2 w-2 rounded-full bg-slate-500" />
                                Not connected
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-slate-300">
                          <span className="font-semibold text-slate-200">Discord ID:</span>{" "}
                          {profile?.discord_id || "—"}
                        </div>
                        <div className="text-xs text-slate-300">
                          <span className="font-semibold text-slate-200">Linked on:</span>{" "}
                          {profile?.discord_connected_at ? fmt(profile.discord_connected_at) : "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {!discordConnected ? (
                      <button
                        onClick={connectDiscord}
                        className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-indigo-500"
                      >
                        Connect Discord
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => ensureDiscordRole({ showMessages: true, debug: false })}
                          disabled={ensuring}
                          className="inline-flex items-center rounded-md border border-slate-600 bg-slate-950 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-900 disabled:opacity-60"
                        >
                          {ensuring ? "Fixing…" : "Fix my Discord role"}
                        </button>

                        <button
                          onClick={async () => {
                            await ensureDiscordRole({ showMessages: true, debug: true });
                            setDebugOpen(true);
                          }}
                          className="inline-flex items-center rounded-md border border-slate-600 bg-slate-950 px-3 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-900"
                        >
                          Run with debug
                        </button>

                        <button
                          disabled={saving}
                          onClick={disconnectDiscord}
                          className="inline-flex items-center rounded-md border border-red-400/70 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-100 hover:bg-red-500/20 disabled:opacity-60"
                        >
                          Disconnect
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <p className="mt-3 text-[11px] text-slate-400">
                  Connecting Discord lets us automatically assign server roles
                  based on your plan so you get access to the right channels.
                </p>

                {/* Debug details */}
                {debugJson && (
                  <details open={debugOpen} className="mt-3">
                    <summary
                      className="cursor-pointer select-none text-xs text-slate-300 hover:text-slate-100"
                      onClick={() => setDebugOpen((v) => !v)}
                    >
                      {debugOpen ? "Hide" : "Show"} role-sync debug details
                    </summary>
                    <pre className="m-0 mt-2 max-h-72 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-sky-200">
{JSON.stringify(debugJson, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>

            {/* Debug data */}
            <div className="mt-6">
              <details className="rounded-xl border border-slate-800/80 bg-slate-950/90">
                <summary className="cursor-pointer select-none rounded-xl px-4 py-2 text-sm font-medium text-slate-100">
                  Debug data
                </summary>
                <pre className="m-0 max-h-80 overflow-auto rounded-b-xl bg-slate-950 p-4 text-xs text-sky-200">
{JSON.stringify({ user, profile }, null, 2)}
                </pre>
              </details>
            </div>
          </div>
        </div>
      </div>

      {/* Toast viewport */}
      <ToastViewport toasts={toasts} removeNow={removeNow} />
    </>
  );
}
